import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FiPlus,
  FiSearch,
  FiEdit,
  FiTrash2,
  FiPackage,
} from 'react-icons/fi';
import { productService } from '../services/apiService';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatCurrency, getStockStatus } from '../utils/formatters';

const Products = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');

  const { data, isLoading, error } = useQuery(
    'products',
    productService.getAll
  );

  const deleteMutation = useMutation(productService.delete, {
    onSuccess: () => {
      toast.success('Product deleted successfully');
      queryClient.invalidateQueries('products');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to delete product');
    },
  });

  if (isLoading) return <LoadingSpinner message="Loading products..." />;

  if (error) {
    return (
      <div className="empty-state">
        <FiPackage size={48} />
        <h3>Failed to load products</h3>
        <p>{error.message}</p>
      </div>
    );
  }

  const products = data?.data || [];

  const filteredProducts = products.filter(
    (product) =>
      product.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.barcode?.includes(searchTerm) ||
      product.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = (product) => {
    if (window.confirm(`Delete "${product.product_name}"?`)) {
      deleteMutation.mutate(product.product_id);
    }
  };

  // Helper to render a price cell that shows USD and/or LRD
  const renderPrice = (product, type) => {
    const usd = product[`${type}_usd`];
    const lrd = product[`${type}_lrd`];
    const legacy = product[type];

    const hasUsd = usd != null && parseFloat(usd) > 0;
    const hasLrd = lrd != null && parseFloat(lrd) > 0;

    if (hasUsd && hasLrd) {
      return (
        <div>
          <div style={{ fontWeight: 600 }}>
            {formatCurrency(parseFloat(usd))}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            L${parseFloat(lrd).toFixed(2)}
          </div>
        </div>
      );
    }
    if (hasUsd) {
      return <strong>{formatCurrency(parseFloat(usd))}</strong>;
    }
    if (hasLrd) {
      return <strong>L${parseFloat(lrd).toFixed(2)}</strong>;
    }
    // Legacy fallback
    return <span>{formatCurrency(legacy)}</span>;
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Products</h1>
          <p className="page-subtitle">
            Manage your inventory products ({products.length} total)
          </p>
        </div>
        <div className="page-actions">
          <button
            className="btn btn-primary"
            onClick={() => navigate('/products/new')}
          >
            <FiPlus size={18} />
            Add Product
          </button>
        </div>
      </div>

      <div className="search-bar">
        <FiSearch size={20} />
        <input
          type="text"
          placeholder="Search products by name, barcode, or category..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {filteredProducts.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <FiPackage size={48} />
            <h3>No products found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search'
                : 'Start by adding your first product'}
            </p>
            {!searchTerm && (
              <button
                className="btn btn-primary"
                onClick={() => navigate('/products/new')}
              >
                <FiPlus size={18} />
                Add Product
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Category</th>
                <th>Barcode</th>
                <th>Selling Price</th>
                <th>Cost Price</th>
                <th>Stock</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => {
                const status = getStockStatus(
                  product.current_stock || 0,
                  product.reorder_level || 0
                );
                return (
                  <tr key={product.product_id}>
                    <td>
                      <strong>{product.product_name}</strong>
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#9ca3af',
                        }}
                      >
                        {product.unit}
                      </div>
                    </td>
                    <td>{product.category || '-'}</td>
                    <td>{product.barcode || '-'}</td>
                    <td>{renderPrice(product, 'selling_price')}</td>
                    <td>{renderPrice(product, 'cost_price')}</td>
                    <td>{product.current_stock || 0}</td>
                    <td>
                      <span className={`badge badge-${status.color}`}>
                        {status.label}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="icon-btn"
                          onClick={() =>
                            navigate(`/products/edit/${product.product_id}`)
                          }
                          title="Edit"
                        >
                          <FiEdit size={16} />
                        </button>
                        <button
                          className="icon-btn"
                          onClick={() => handleDelete(product)}
                          title="Delete"
                          style={{ color: '#ef4444' }}
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Products;
